import React, { useState, useCallback, useRef, useEffect } from 'react'
import { SectionHeader } from '../Core/SectionHeader'
import apartmentBuilding from '../ModelViewer/ApartmentBuilding'
import ApartmentList from '../ModelViewer/AppartmentList'
import { mainApartments } from '../ModelViewer/UnitList'
import BuildingViewer from '../ModelViewer/BuildingViewer'
import { ApartmentDetails, samplePanoramaNodes } from '../Engine/Components/ApartmentDetails'
import { PanoramaView } from '../Engine/Components/PanoramaView'
import { ApartmentRoom, PanoramaNode } from '../Engine/types'

export function ShowcaseSection (): JSX.Element {
  const [selectedApartment, setSelectedApartment] = useState<any>(null)
  const [selectedRoom, setSelectedRoom] = useState<{ room: ApartmentRoom; node: PanoramaNode } | null>(null)
  const [viewMode, setViewMode] = useState<'building' | 'panorama'>('building')
  const buildingViewerRef = useRef<any>(null)
  const [hoveredApartment, setHoveredApartment] = useState<{ id: string; floor: number } | null>(null)
  const [nodeAngleFov, setNodeAngleFov] = useState<number | null>(null)

  const handleApartmentSelected = useCallback((apartment: any) => {
    setSelectedApartment(apartment)
  }, [])

  const handleBackToList = useCallback(() => {
    setSelectedApartment(null)
    setSelectedRoom(null)
    setViewMode('building')
    setNodeAngleFov(null)
    // Deselect apartment in 3D view
    if (buildingViewerRef.current) {
      buildingViewerRef.current.deselectApartment()
    }
  }, [])

  const handleSelectRoom = useCallback((room: ApartmentRoom, node: PanoramaNode) => {
    setSelectedRoom({ room, node })
    setViewMode('panorama')
  }, [])

  const handleHotspotClick = useCallback((hotspot: any) => {
    // Find the target node from the imported sample nodes
    const targetNode = samplePanoramaNodes.find((node: PanoramaNode) => node.id === hotspot.targetPanoramaId)

    if (!targetNode) {
      console.warn(`Target node with id ${hotspot.targetPanoramaId} not found`)
      return
    }

    // Create a mock room based on the hotspot
    const targetRoom: ApartmentRoom = {
      id: hotspot.targetPanoramaId,
      name: hotspot.targetRoomName,
      type: hotspot.targetRoomName.toLowerCase(),
      size: 25,
      floor: selectedApartment?.floor || 1,
      windowCount: 2,
      hasBalcony: false,
      amenities: [],
      description: `360° view of the ${hotspot.targetRoomName.toLowerCase()}`,
      panoramaNode: []
    }

    setSelectedRoom({ room: targetRoom, node: targetNode })
  }, [selectedApartment])

  // Context-aware back handler for apartment details
  const handleApartmentDetailsBack = useCallback(() => {
    if (viewMode === 'panorama') {
      // If in panorama view, just go back to building view but keep apartment details
      setViewMode('building')
      setSelectedRoom(null)
      setNodeAngleFov(null)
    } else {
      // If in building view, go back to apartment list
      handleBackToList()
    }
  }, [viewMode, handleBackToList])

  const handleApartmentHover = useCallback((apartmentInfo: { id: string; floor: number } | null) => {
    setHoveredApartment(apartmentInfo)
  }, [])

  // Clear hover state when apartment is selected to prevent conflicts
  useEffect(() => {
    if (selectedApartment) {
      setHoveredApartment(null)
    }
  }, [selectedApartment])

  const handleApartmentListClick = useCallback((apartmentInfo: { id: string; floor: number }) => {
    const baseApartmentId = apartmentInfo.id.split('-')[0] // Extract A1, B2, etc.
    const apartmentData = mainApartments.find(apt => apt.id === baseApartmentId)

    if (!apartmentData) {
      console.warn(`Apartment data not found for ${baseApartmentId}`)
      return
    }

    const mockApartment = {
      id: apartmentInfo.id,
      name: `Apartment ${apartmentInfo.id}`,
      floor: apartmentInfo.floor,
      area: apartmentData.area,
      price: apartmentData.price,
      apt_plans: apartmentData.apt_plans,
      outline: [],
      holes: [],
      rooms: [
        {
          id: '1',
          name: 'Living Room',
          type: 'living',
          size: 25.5,
          floor: apartmentInfo.floor,
          windowCount: 2,
          hasBalcony: true,
          amenities: ['Air Conditioning', 'Hardwood Floors'],
          description: 'Spacious living room with natural light',
          panoramaNode: []
        },
        {
          id: '2',
          name: 'Bedroom',
          type: 'bedroom',
          size: 18.5,
          floor: apartmentInfo.floor,
          windowCount: 1,
          hasBalcony: false,
          amenities: ['Built-in Closet'],
          description: 'Comfortable bedroom with great views',
          panoramaNode: []
        },
        {
          id: '3',
          name: 'Kitchen',
          type: 'kitchen',
          size: 12.0,
          floor: apartmentInfo.floor,
          windowCount: 1,
          hasBalcony: false,
          amenities: ['Modern Appliances', 'Island Counter'],
          description: 'Modern kitchen with premium finishes',
          panoramaNode: []
        }
      ],
      topdownImageUrl: '/apartment_footprint.png'
    }

    setSelectedApartment(mockApartment)
  }, [])

  return (
    <section className="px-4 lg:px-16 bg-gray-50 text-black py-16">
      <SectionHeader
        eyebrow=""
        title=""
      />
      <div className="bg-gray-200 rounded-3xl p-2">
        <div className="flex flex-col xl:flex-row gap-2 h-fit">
          {/* Left: 3D Model Viewer OR Panorama View - ALWAYS same size */}
          <div className="lg:basis-2/3 aspect-[4/3] relative">
            {viewMode === 'building' ? (
              <BuildingViewer
                ref={buildingViewerRef}
                building={apartmentBuilding}
                onApartmentSelected={handleApartmentSelected}
                hideUIOnRotation={false}
                selectedApartment={selectedApartment}
                hoveredApartment={hoveredApartment}
              />
            ) : (
              <div className="w-full h-full relative overflow-hidden rounded-2xl">
                {selectedRoom && (
                  <>
                    <PanoramaView
                      imageUrl={selectedRoom.node.panoramaUrl}
                      initialRotation={selectedRoom.node.initialRotation}
                      setNodeAngleFov={setNodeAngleFov}
                      className="w-full h-full"
                      hotspots={selectedRoom.node.hotspots || []}
                      onHotspotClick={handleHotspotClick}
                    />
                    {/* Room info */}
                    <div className="hidden md:block absolute bottom-4 left-4 bg-white/90 rounded-lg p-3 shadow-lg">
                      <h3 className="font-semibold text-lg">
                        {selectedRoom.room.name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {selectedRoom.room.description}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {selectedRoom.room.size}㎡ • Floor{' '}
                        {selectedRoom.room.floor}
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Right: Apartment List OR Apartment Details - Fixed height matching left side */}
          <div className="lg:basis-1/3 xl:aspect-[3/4] flex">
            {selectedApartment ? (
              <ApartmentDetails
                footprint={selectedApartment}
                onBack={handleApartmentDetailsBack}
              />
            ) : (
              <ApartmentList onApartmentHover={handleApartmentHover} onApartmentClick={handleApartmentListClick}/>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
